// ─────────────────────────────────────────────────────────
//  clickByHandGesture.js  –  fist → open palm = click
// ─────────────────────────────────────────────────────────

let clickControl = {
  state: 'idle',
  lastGestureTime: 0,
  fistAverage: { x: 0, y: 0 },
  smoothAvg: null,
};

const EMA_ALPHA = 0.35; // smoothing factor for centroid
const FIST_STABLE_MS = 180; // ms fist must be held before arming
const MOVEMENT_STABLE_PX = 14; // px – max drift allowed while arming
const COOLDOWN_MS = 800; // ms between successive clicks

// ── Fist thresholds ───────────────────────────────────────
// A fist = all four fingertips curled close to their MCP (knuckle) joints.
// We measure each fingertip→ MCP distance; if ALL are below FIST_TIP_MCP_MAX
// the hand is considered a fist.
const FIST_TIP_MCP_MAX = 50; // px – tip must be within this of its MCP
const FIST_MIN_FINGERS = 4; // how many fingers must satisfy the above (thumb excluded)

// ── Open-palm threshold ───────────────────────────────────
// Open palm = fingertips spread far from the palm centre.
// We reuse the same spread metric as before.
const OPEN_DISTANCE_THRESHOLD = 110; // px – max spread from centroid

const NEUTRAL_GRACE_MS = 400; // ms of neutral pose before disarming
let neutralSince = 0;
// ─────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────

/**
 * Returns true when the four fingers (index→pinky) are curled into a fist.
 * Strategy: each fingertip should be close to BOTH its MCP knuckle AND the palm base.
 *
 * Handpose annotation indices used:
 *   [0] = MCP (knuckle),  [3] = fingertip
 */
function isFist(annotations) {
  const palmBase = annotations.palmBase[0];

  const PALM_BASE_MAX = 120; // px – tip must also be within this of the palm base

  const fingers = [
    { mcp: annotations.indexFinger[0], tip: annotations.indexFinger[3] },
    { mcp: annotations.middleFinger[0], tip: annotations.middleFinger[3] },
    { mcp: annotations.ringFinger[0], tip: annotations.ringFinger[3] },
    { mcp: annotations.pinky[0], tip: annotations.pinky[3] },
  ];

  let curledCount = 0;
  for (const { mcp, tip } of fingers) {
    const distMCP = Math.hypot(tip[0] - mcp[0], tip[1] - mcp[1]);
    const distPalm = Math.hypot(tip[0] - palmBase[0], tip[1] - palmBase[1]);

    if (distMCP < FIST_TIP_MCP_MAX && distPalm < PALM_BASE_MAX) curledCount++;
  }

  return curledCount >= FIST_MIN_FINGERS;
}

/**
 * Returns true when the hand is open (fingertips spread away from centroid).
 */
function isOpenPalm(annotations) {
  const tips = [
    annotations.thumb[3],
    annotations.indexFinger[3],
    annotations.middleFinger[3],
    annotations.ringFinger[3],
    annotations.pinky[3],
  ];

  const avgX = tips.reduce((s, p) => s + p[0], 0) / 5;
  const avgY = tips.reduce((s, p) => s + p[1], 0) / 5;

  let maxDist = 0;
  for (const tip of tips) {
    const d = Math.hypot(tip[0] - avgX, tip[1] - avgY);
    if (d > maxDist) maxDist = d;
  }
  return maxDist > OPEN_DISTANCE_THRESHOLD;
}

// ─────────────────────────────────────────────────────────
//  Main export
// ─────────────────────────────────────────────────────────
export function detectFistToOpenPalmClickGesture(prediction) {
  const now = Date.now();
  const ann = prediction.annotations;

  // ── Smooth the wrist position for stability checks ────
  const wrist = prediction.landmarks[0]; // [x, y, z]
  if (!clickControl.smoothAvg) {
    clickControl.smoothAvg = { x: wrist[0], y: wrist[1] };
  } else {
    clickControl.smoothAvg.x = EMA_ALPHA * wrist[0] + (1 - EMA_ALPHA) * clickControl.smoothAvg.x;
    clickControl.smoothAvg.y = EMA_ALPHA * wrist[1] + (1 - EMA_ALPHA) * clickControl.smoothAvg.y;
  }

  const movement = Math.hypot(
    clickControl.smoothAvg.x - clickControl.fistAverage.x,
    clickControl.smoothAvg.y - clickControl.fistAverage.y,
  );
  const stable = movement < MOVEMENT_STABLE_PX;
  clickControl.fistAverage = { x: clickControl.smoothAvg.x, y: clickControl.smoothAvg.y };

  // ── Gesture detection ─────────────────────────────────
  const fistDetected = isFist(ann);
  const openPalmDetected = isOpenPalm(ann);

  // ── Visual cursor feedback (driven by STATE, not raw detection) ─────
  const cursor = document.getElementById('virtual-cursor');
  if (cursor) {
    if (fistDetected) {
      cursor.classList.add('cluster'); // red = fist armed
      cursor.classList.remove('open');
    } else {
      cursor.classList.add('open'); // purple = relaxed / open
      cursor.classList.remove('cluster');
    }
  }

  // ── State machine ─────────────────────────────────────
  switch (clickControl.state) {
    // Waiting for a fist
    case 'idle':
      if (fistDetected && stable) {
        clickControl.state = 'fist';
        clickControl.lastGestureTime = now;
      }
      break;

    // Holding fist – confirm it's intentional, not accidental
    case 'fist':
      if (!fistDetected) {
        clickControl.state = 'idle'; // opened too soon – abort
        break;
      }
      if (!stable) {
        clickControl.lastGestureTime = now; // drifting – restart hold timer
        break;
      }
      if (now - clickControl.lastGestureTime > FIST_STABLE_MS) {
        clickControl.state = 'waitingOpen';
        console.log('[gesture] Fist armed – open palm to click');
      }
      break;

    // Fist is armed – wait for open palm to fire click
    case 'waitingOpen':
      if (openPalmDetected) {
        console.log('[gesture] CLICK TRIGGERED ✓');
        fireClick();
        neutralSince = 0;
        clickControl.state = 'clicked';
        break;
      }
      // Hand is in neutral (neither fist nor open) — give it NEUTRAL_GRACE_MS
      // to finish opening before disarming, so the transition doesn't abort early.
      if (!fistDetected && !openPalmDetected) {
        if (neutralSince === 0) neutralSince = now;
        if (now - neutralSince > NEUTRAL_GRACE_MS) {
          clickControl.state = 'idle';
          neutralSince = 0;
        }
      } else {
        neutralSince = 0; // still moving toward open — keep waiting
      }
      break;

    // Debounce – wait for neutral pose before allowing next click
    case 'clicked':
      if (!fistDetected && !openPalmDetected) {
        clickControl.state = 'idle';
      }
      break;
  }
}

// ─────────────────────────────────────────────────────────
//  "Open Google" button listener
// ─────────────────────────────────────────────────────────
const openUrl = 'https://www.google.com';

// ─────────────────────────────────────────────────────────
//  Fire click directly on #open-google, regardless of where
//  the hand / cursor currently is on screen.
// ─────────────────────────────────────────────────────────
function fireClick() {
  const btn = document.getElementById('open-google');

  const cursor = document.getElementById('virtual-cursor');

  // ── Apply feedback immediately so the browser paints it ──
  // virtual-hover must be added BEFORE window.open() is called,
  // otherwise focus shifts to the new tab before the frame renders.
  if (btn) {
    btn.classList.add('virtual-hover');
    // Force a reflow so the class is painted this frame
    void btn.offsetWidth;
    setTimeout(() => btn.classList.remove('virtual-hover'), 2000);
  }

  if (cursor) {
    cursor.classList.remove('cluster', 'open');
    cursor.classList.add('fired');
    setTimeout(() => cursor.classList.remove('fired'), 2000);
  }

  // Open the tab after a short delay so the feedback frame is visible first

  const tab = window.open(openUrl, '_blank', 'noopener,noreferrer');
  if (tab) tab.focus();
}
